import React, { useState, useCallback, useEffect } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    StyledDropMenu,
    StyledItem,
    UnreadBadge,
    Header,
    HeaderTexts,
    HeaderTitle,
    HeaderSubtitle,
    HeaderCloseIcon,
    ListWrapper,
    EmptyState,
    Footer,
    MarkAllReadButton
} from './style';
import { Dropdown, DropdownToggle, useDropdownContext } from '../../components/ui/dropdown/dropdown';
import { SERVICEURL } from '../../appconfig';
import deleteNoti from "../../shared/images/icons/deleteNoti.svg";
import clockNoti from "../../shared/images/icons/clockNoti.svg";
import closeNoti from "../../shared/images/icons/closeNoti.svg";
import notBildirim from "../../shared/images/icons/notBildirim.svg";
import { useToastContext } from '../../context/ToastContextProvider';

interface NotificationData {
    NotificationID: string;
    NotificationTypeID: number;
    NotificationStatusID: number;
    NotificationDate: string;
    Message: string;
    Title?: string;
    candidateId?: string;
}

const NotificationsHeader: React.FC<{ subtitle: string }> = ({ subtitle }) => {
    const dropdown = useDropdownContext();

    const handleClose = () => {
        dropdown?.close();
    };

    return (
        <Header>
            <HeaderTexts>
                <HeaderTitle>Bildirimler</HeaderTitle>
                <HeaderSubtitle>{subtitle}</HeaderSubtitle>
            </HeaderTexts>
            <HeaderCloseIcon onClick={handleClose}>
                <img src={closeNoti} alt="Close" />
            </HeaderCloseIcon>
        </Header>
    );
};

function Notifications() {
    const [notifications, setNotifications] = useState<NotificationData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [unreadCount, setUnreadCount] = useState<number>(0);
  const { showToast } = useToastContext();

    useEffect(() => {
        let isMounted = true;
        const mytoken = localStorage.getItem('token');

        const fetchNotifications = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(
                    `${SERVICEURL}api/Notification/GetUserNotification`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${mytoken}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`Status code: ${response.status}`);
                }

                const data: any = await response.json();

                if (!isMounted) return;

                // Beklenen response:
                // { code: 200, message: "Bildirim bilgisi.", data: [{ id, title, message, createdAt, isRead, isCompleted }] }
                let items: any[] = Array.isArray(data) ? data : data?.data ?? [];

                // createdAt alanına göre en yeni bildirim en üstte olacak şekilde sırala
                items = items.sort((a, b) => {
                    const aDate = new Date(a.createdAt ?? a.CreatedAt ?? 0).getTime();
                    const bDate = new Date(b.createdAt ?? b.CreatedAt ?? 0).getTime();
                    return bDate - aDate;
                });

                const mapped: NotificationData[] = items.map((item: any) => ({
                    NotificationID: String(item.id ?? ''),
                    NotificationTypeID: 0,
                    NotificationStatusID: item.isRead ? 2 : 1,
                    NotificationDate: item.createdAt ?? new Date().toISOString(),
                    Message:
                        item.title && item.message
                            ? `${item.title}\n${item.message}`
                            : item.message ?? 'Yeni bildiriminiz var',
                    Title: item.title ?? '',
                    candidateId: item.candidateId ?? item.candidateID ?? item.candidateid,
                }));

                setNotifications(mapped);

                // Liste içinden unread count da hesaplayalım (gerekirse backend sayısıyla override edilir)
                const initialUnread = mapped.filter((n) => n.NotificationStatusID === 1).length;
                setUnreadCount(initialUnread);
            } catch (err: any) {
                if (!isMounted) return;
                setError('Bildirimler yüklenirken bir hata oluştu.');
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        const fetchUnreadCount = async () => {
            try {
                if (!mytoken) return;

                const response = await fetch(
                    `${SERVICEURL}api/Notification/GetCountOfUnreadNotificationsForUser`,
                    {
                        method: 'GET',
                        headers: {
                            accept: 'text/plain',
                            Authorization: `Bearer ${mytoken}`,
                        },
                    }
                );

                if (!response.ok) return;

                const text = await response.text();
                const count = Number(text);
                if (!Number.isNaN(count) && isMounted) {
                    setUnreadCount(count);
                }
            } catch {
                // count hatasında UI bozmayalım, sessiz geçiyoruz
            }
        };

        if (mytoken) {
            // İlk yükleme
            fetchNotifications();
            fetchUnreadCount();

            // Belirli aralıklarla (5 dk) yeni bildirimleri ve sayacı kontrol et
            const intervalId = setInterval(() => {
                if (!isMounted) return;
                fetchNotifications();
                fetchUnreadCount();
            }, 300000);

            return () => {
                isMounted = false;
                clearInterval(intervalId);
            };
        }

        setError('Oturum bulunamadı.');

        return () => {
            isMounted = false;
        };
    }, []);

    const handleItemClick = useCallback((id: string) => {
        // Bildirimin candidateId'si varsa yönlendir
        const notification = notifications.find((item) => item.NotificationID === id);
        const candidateId = notification?.candidateId;
        if (candidateId) {
            window.location.href = `${window.location.origin}/candidate-detail/${candidateId}`;
        } else {
           showToast('Bu bildirime ait aday bilgisi bulunamadı', 'warning');
        }
    }, [notifications, showToast]);

    const handleMarkAsRead = useCallback(async (id: string) => {
        // Önce local state'i güncelle (optimistic update)
        setNotifications((prev) =>
            prev.map((item) =>
                item.NotificationID === id
                    ? { ...item, NotificationStatusID: 2 }
                    : item
            )
        );

        // Unread sayısını da azalt
        setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));

        try {
            const mytoken = localStorage.getItem('token');
            if (!mytoken) return;

            // API artık body'de ID array'i bekliyor
            await fetch(`${SERVICEURL}api/Notification/UpdateReadNotifications`, {
                method: 'POST',
                headers: {
                    accept: 'text/plain',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${mytoken}`,
                },
                body: JSON.stringify([id]),
            });
        } catch (e) {
            // Şimdilik hata durumunda sadece konsola yaz; UI'de değişiklik yapma
            // eslint-disable-next-line no-console
            console.error('Bildirim okundu güncellenemedi', e);
        }
    }, []);

    const handleDelete = useCallback(async (id: string) => {
        // Optimistic: önce listeden kaldır
        setNotifications((prev) => prev.filter((item) => item.NotificationID !== id));

        setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));

        try {
            const mytoken = localStorage.getItem('token');
            if (!mytoken) return;

            await fetch(`${SERVICEURL}api/Notification/CompleteNotifications`, {
                method: 'POST',
                headers: {
                    accept: 'text/plain',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${mytoken}`,
                },
                body: JSON.stringify([id]),
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Bildirim silinirken hata oluştu', e);
        }
    }, []);

    const handleDeleteAll = useCallback(async () => {
        const allIds = notifications.map((n) => n.NotificationID);
        if (allIds.length === 0) return;
        // Optimistic: tüm bildirimleri listeden kaldır
        setNotifications([]);
        setUnreadCount(0);
        try {
            const mytoken = localStorage.getItem('token');
            if (!mytoken) return;
            await fetch(`${SERVICEURL}api/Notification/CompleteNotifications`, {
                method: 'POST',
                headers: {
                    accept: 'text/plain',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${mytoken}`,
                },
                body: JSON.stringify(allIds),
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Tüm bildirimler silinirken hata oluştu', e);
        }
    }, [notifications]);

    const handleMarkAllRead = useCallback(async () => {
        const unreadIds = notifications
            .filter((n) => n.NotificationStatusID === 1)
            .map((n) => n.NotificationID);

        if (unreadIds.length === 0) return;

        // Optimistic: tüm okunmamışları okunduya çevir
        setNotifications((prev) =>
            prev.map((n) =>
                n.NotificationStatusID === 1
                    ? { ...n, NotificationStatusID: 2 }
                    : n
            )
        );

        // Tüm okunmamışlar okundu olacak
        setUnreadCount(0);

        try {
            const mytoken = localStorage.getItem('token');
            if (!mytoken) return;

            await fetch(`${SERVICEURL}api/Notification/UpdateReadNotifications`, {
                method: 'POST',
                headers: {
                    accept: 'text/plain',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${mytoken}`,
                },
                body: JSON.stringify(unreadIds),
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Tüm okunmamış bildirimler okundu olarak işaretlenemedi', e);
        }
    }, [notifications]);

    const headerSubtitleText = loading
        ? 'Bildirimler yükleniyor'
        : unreadCount > 0
        ? `${unreadCount} yeni bildiriminiz var`
        : 'Yeni bildiriminiz yok';

    const formatRelativeTime = (dateStr: string) => {
        const date = new Date(dateStr);
        const diffMs = Date.now() - date.getTime();
        const diffMinutes = Math.max(1, Math.round(diffMs / (1000 * 60)));

        if (diffMinutes < 60) {
            return `${diffMinutes} dakika önce`;
        }

        const diffHours = Math.max(1, Math.round(diffMinutes / 60));
        return `${diffHours} saat önce`;
    };
    return (
        <div>
            <Dropdown direction="down" className="dropdown-profile">
                <DropdownToggle variant="texted">
                    <NotificationsIcon className="header-icon" />
                    {unreadCount > 0 && <UnreadBadge />}
                </DropdownToggle>
                <StyledDropMenu>
                    <NotificationsHeader subtitle={headerSubtitleText} />

                    {loading ? (
                        <EmptyState>Yükleniyor...</EmptyState>
                    ) : error ? (
                        <EmptyState>{error}</EmptyState>
                    ) : notifications.length === 0 ? (
                        <EmptyState>
                            <img src={notBildirim} alt="Bildirim Yok" className="empty-icon" />
                            <h3>Bildirim Yok</h3>
                            <p>Yeni bildiriminiz yok.<br />Geldiğinde burada görünecekler.</p>
                        </EmptyState>
                    ) : (
                        <ListWrapper>
                            {notifications.map((notification: NotificationData) => (
                                <StyledItem
                                    key={notification.NotificationID}
                                    className={notification.NotificationStatusID === 1 ? 'unread' : 'read'}
                                >
                                    <div 
                                        className="content"
                                        style={{cursor:'pointer'}}
                                        onClick={() => handleItemClick(notification.NotificationID)}
                                    >
                                        <h4>{notification.Title}</h4>
                                        <p>{notification.Message}</p>
                                        <span className="time">
                                            <img src={clockNoti} alt="Clock" /> {formatRelativeTime(notification.NotificationDate)}
                                        </span>
                                               {notification.NotificationStatusID === 1 && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="mark-read-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMarkAsRead(notification.NotificationID);
                                                    }}
                                                    title="Okundu olarak işaretle"
                                                >
                                                    Okundu olarak işaretle
                                                </button>
                                                <span className="unread-dot" />
                                            </>
                                        )}
                                    </div>
                                    <div className="actions">
                                 
                                        <button
                                            type="button"
                                            className="delete-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(notification.NotificationID);
                                            }}
                                            title="Sil"
                                        >
                                            <img src={deleteNoti} alt="Delete" />
                                        </button>
                                    </div>
                                </StyledItem>
                            ))}
                        </ListWrapper>
                    )}

                    {notifications.length > 0 && !loading && (
                        <Footer>
                            <MarkAllReadButton type="button" onClick={handleMarkAllRead}>
                                Tümünü Okundu Olarak İşaretle
                            </MarkAllReadButton>
                            <MarkAllReadButton type="button" onClick={handleDeleteAll}>
                                Tümünü Sil
                            </MarkAllReadButton>
                        </Footer>
                    )}
                </StyledDropMenu>
            </Dropdown>
        </div>
    );
}

export default Notifications;
